import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  Avatar,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '../../services/api';
import orgDocumentService from '../../services/orgDocumentService';
import { getAgentForTask } from '../../utils/agentNames';

const FieldValidationDialog = ({
  open,
  onClose,
  task = {},
  user = {},
  projectId,
}) => {
  const [docMode, setDocMode] = useState('infanion_default');
  const [infanionDoc, setInfanionDoc] = useState(null);
  const [infanionExists, setInfanionExists] = useState(false);
  const [checkingDefault, setCheckingDefault] = useState(true);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);

  const agentInfo = getAgentForTask(task);
  const taskAgent = agentInfo || { name: 'Fieldy', role: 'Field Validations' };

  // Check if Infanion default document exists
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    const checkDefault = async () => {
      setCheckingDefault(true);
      try {
        const result = await orgDocumentService.getByType('FIELD_VALIDATION');
        if (mounted) {
          setInfanionExists(result.exists);
          setInfanionDoc(result.data);
        }
      } catch (err) {
        console.error('Error checking Infanion default:', err);
        if (mounted) { setInfanionExists(false); setInfanionDoc(null); }
      } finally {
        if (mounted) setCheckingDefault(false);
      }
    };
    checkDefault();
    return () => { mounted = false; };
  }, [open]);

  // Fetch avatar
  useEffect(() => {
    if (!open || !task) return;
    const fetchAvatar = async () => {
      try {
        const sectionId = task?.sectionId || task?.section?._id || task?.section;
        if (!sectionId) return;
        const response = await api.get(`/api/sections/${sectionId}/avatar`);
        if (response.data?.success && response.data?.avatarUrl) {
          setAvatarUrl(response.data.avatarUrl);
        }
      } catch (err) { /* silently fail */ }
    };
    fetchAvatar();
  }, [task, open]);

  const handleFileChange = (e) => {
    setUploadedFile(e.target.files[0] || null);
  };

  const removeFile = () => setUploadedFile(null);

  const needsFileUpload = docMode === 'project_specific' || docMode === 'combine_both';

  const isStartDisabled = () => {
    if (checkingDefault) return true;
    if (docMode === 'infanion_default' && !infanionExists) return true;
    if (needsFileUpload && !uploadedFile) return true;
    return false;
  };

  const handleStartChat = () => {
    const taskId = task?.id || task?._id || task?.taskId || task?.task_id || task?.sectionId;
    if (!projectId || !taskId) return;

    if (uploadedFile && needsFileUpload) {
      window.__pendingFVFile = uploadedFile;
    }

    const params = new URLSearchParams({ task: taskId, doc_mode: docMode });
    if (infanionDoc?.id && (docMode === 'infanion_default' || docMode === 'combine_both')) {
      params.set('org_doc_id', infanionDoc.id);
    }
    window.location.href = `/projects/${projectId}/conversation?${params.toString()}`;
  };

  const avatarImage = avatarUrl
    ? (avatarUrl.startsWith('http') ? avatarUrl : `${process.env.REACT_APP_API_URL || 'http://localhost:8003/api'}${avatarUrl.startsWith('/') ? '' : '/'}${avatarUrl}`)
    : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: 'hidden' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1.5,
          px: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} color="#475969">
          {task?.title || 'Field Validations'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
        {/* Agent Avatar */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2.5 }}>
          <Avatar
            src={avatarImage}
            alt={taskAgent.name}
            sx={{
              width: 64,
              height: 64,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              mb: 1,
              bgcolor: 'transparent',
              border: '2px solid #ffcccc',
              fontSize: '1.5rem',
              color: '#ff6666',
            }}
          >
            {taskAgent.name.charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="subtitle2" fontWeight={600} color="#475969">
            {taskAgent.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
            Choose how to handle field validations for this project.
          </Typography>
        </Box>

        {checkingDefault ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <>
            <RadioGroup value={docMode} onChange={(e) => { setDocMode(e.target.value); setUploadedFile(null); }}>
              {/* Option 1 */}
              <FormControlLabel
                value="infanion_default"
                disabled={!infanionExists}
                control={<Radio size="small" sx={{ py: 0.5 }} />}
                label={
                  <Box sx={{ ml: 0.5 }}>
                    <Typography variant="body2" fontWeight={500} color={infanionExists ? 'text.primary' : 'text.disabled'}>
                      {infanionExists
                        ? 'Use Infanion field validation document'
                        : 'Use Infanion field validation document (not uploaded yet)'}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1, mx: 0 }}
              />

              {/* Option 2 */}
              <FormControlLabel
                value="project_specific"
                control={<Radio size="small" sx={{ py: 0.5 }} />}
                label={
                  <Box sx={{ ml: 0.5 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Upload a document that this project needs to follow
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1, mx: 0 }}
              />

              {/* Option 3 */}
              <FormControlLabel
                value="combine_both"
                control={<Radio size="small" sx={{ py: 0.5 }} />}
                label={
                  <Box sx={{ ml: 0.5 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Combine Infanion field validation with a document you upload
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mx: 0 }}
              />
            </RadioGroup>

            {/* File upload area — shown when project_specific or combine_both is selected */}
            {needsFileUpload && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: '1.5px dashed',
                  borderColor: uploadedFile ? 'success.main' : 'grey.300',
                  borderRadius: 2,
                  bgcolor: uploadedFile ? 'success.50' : 'grey.50',
                  textAlign: 'center',
                }}
              >
                {uploadedFile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <InsertDriveFileIcon fontSize="small" color="success" />
                    <Chip
                      label={uploadedFile.name}
                      size="small"
                      onDelete={removeFile}
                      sx={{ maxWidth: 260 }}
                    />
                  </Box>
                ) : (
                  <>
                    <Button
                      component="label"
                      startIcon={<CloudUploadIcon />}
                      size="small"
                      sx={{ textTransform: 'none', color: '#475969', fontWeight: 500 }}
                    >
                      Upload Field Validation Document
                      <input
                        type="file"
                        hidden
                        accept=".txt,.md,.docx,.pdf,.xlsx,.xls,.xlsm,.xlsb,.csv"
                        onChange={handleFileChange}
                      />
                    </Button>
                    <Typography variant="caption" display="block" color="text.disabled" sx={{ mt: 0.5 }}>
                      Supports .xlsx, .docx, .pdf, .txt, .md, .csv
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none', color: '#475969' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<ChatIcon />}
            disabled={isStartDisabled()}
            onClick={handleStartChat}
            sx={{
              textTransform: 'none',
              bgcolor: '#485869',
              '&:hover': { bgcolor: '#3a4a59' },
              px: 3,
            }}
          >
            Start Chat
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

FieldValidationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  task: PropTypes.object,
  user: PropTypes.object,
  projectId: PropTypes.string,
};

export default FieldValidationDialog;
